import { useEffect, useState, type ReactNode } from "react";
import {
  fetchGoogleDataStatus,
  disconnectGoogleData,
  startBackfill,
  fetchGadsStatus,
  saveGadsDevToken,
  clearGadsDevToken,
  fetchOAuthClients,
  registerOAuthClient,
  deleteOAuthClient,
  type BackfillTarget,
  type GoogleDataStatus,
  type OAuthClient,
} from "../api.js";
import { SystemPage } from "./SystemPage.js";

/**
 * The apps page (`/_apps`, formerly `/_tokens`): one tile per provider /
 * registered external app, connected status on the tile, the selected app's
 * controls below. Per docs/sql/plans.md "Apps page".
 */

type Sel = "ga" | "ads" | "create" | { app: string };

export function AppsView() {
  const [ga, setGa] = useState<GoogleDataStatus | null>(null);
  const [gadsSet, setGadsSet] = useState<boolean | null>(null);
  const [clients, setClients] = useState<OAuthClient[] | null>(null);
  const [sel, setSel] = useState<Sel>("ga");
  const [err, setErr] = useState<string | null>(null);

  const reload = (): Promise<void> =>
    Promise.all([
      fetchGoogleDataStatus().then(setGa),
      fetchGadsStatus().then((s) => setGadsSet(s.set)),
      fetchOAuthClients().then(setClients),
    ])
      .then(() => {})
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));

  useEffect(() => {
    void reload();
    const params = new URLSearchParams(window.location.search);
    const callbackErr = params.get("google-data-error");
    if (callbackErr) {
      setErr(decodeURIComponent(callbackErr));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!ga || gadsSet === null || !clients) {
    return (
      <SystemPage title="Apps">
        <p className="dw-muted">Loading…</p>
      </SystemPage>
    );
  }

  const hasAdsScope = ga.scopes.some((s) => s.includes("adwords"));
  const hasGaScope = ga.scopes.some((s) => s.includes("analytics"));
  const selApp =
    typeof sel === "object" ? clients.find((c) => c.clientId === sel.app) : null;

  return (
    <SystemPage
      title="Apps"
      lead="Connections datawiki uses to pull data, and external apps allowed to query it. Authorize a source once and its data lands as queryable tables."
    >
      <div className="dw-apps-grid">
        <AppTile
          glyph="GA"
          name="Google Analytics"
          selected={sel === "ga"}
          onClick={() => setSel("ga")}
          ok={ga.connected && hasGaScope}
          status={
            !ga.configured
              ? "not configured"
              : ga.connected
                ? `✓ ${ga.email ?? "connected"}`
                : "not connected"
          }
          detail={ga.connected ? scopeLine(hasGaScope, hasAdsScope) : null}
        />
        <AppTile
          glyph="Ads"
          name="Google Ads"
          selected={sel === "ads"}
          onClick={() => setSel("ads")}
          ok={gadsSet && hasAdsScope}
          status={gadsSet ? "✓ developer token" : "no developer token"}
          detail={ga.connected ? scopeLine(hasGaScope, hasAdsScope) : null}
        />
        {clients.map((c) => (
          <AppTile
            key={c.clientId}
            glyph={(c.name[0] ?? "?").toUpperCase()}
            name={c.name}
            selected={typeof sel === "object" && sel.app === c.clientId}
            onClick={() => setSel({ app: c.clientId })}
            ok
            status="external app"
          />
        ))}
        <AppTile
          glyph="+"
          name="Create App"
          selected={sel === "create"}
          onClick={() => setSel("create")}
          ok={null}
          status="register an external app"
        />
      </div>

      {err && <pre className="dw-error">{err}</pre>}

      {sel === "ga" && (
        <GooglePanel status={ga} reload={reload} setErr={setErr} />
      )}
      {sel === "ads" && (
        <GadsPanel
          tokenSet={gadsSet}
          gaStatus={ga}
          hasAdsScope={hasAdsScope}
          reload={reload}
          setErr={setErr}
        />
      )}
      {sel === "create" && (
        <CreateAppPanel
          onCreated={async (id) => {
            await reload();
            setSel({ app: id });
          }}
          setErr={setErr}
        />
      )}
      {selApp && (
        <AppPanel
          client={selApp}
          onDeleted={async () => {
            setSel("create");
            await reload();
          }}
          setErr={setErr}
        />
      )}
    </SystemPage>
  );
}

function scopeLine(ga: boolean, ads: boolean): string {
  return `analytics ${ga ? "✓" : "✗"} · adwords ${ads ? "✓" : "✗"}`;
}

function AppTile({
  glyph,
  name,
  status,
  detail,
  ok,
  selected,
  onClick,
}: {
  glyph: string;
  name: string;
  status: string;
  detail?: string | null;
  /** true → ok color, false → off color, null → neutral (action tile). */
  ok: boolean | null;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`dw-app-tile${selected ? " selected" : ""}`}
      onClick={onClick}
    >
      <span className="dw-app-glyph">{glyph}</span>
      <span className="dw-app-name">{name}</span>
      <span
        className={`dw-app-status${ok === true ? " ok" : ok === false ? " off" : ""}`}
      >
        {status}
        {detail && <span className="dw-app-detail">{detail}</span>}
      </span>
    </button>
  );
}

/** Shared shape for a panel: status line → action(s) → error. */
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="dw-system-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function BackfillButton({
  target,
  label,
  disabled,
  setErr,
}: {
  target: BackfillTarget;
  label: string;
  disabled?: boolean;
  setErr: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const run = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    setStarted(false);
    try {
      await startBackfill(target);
      setStarted(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="dw-settings-actions">
        <button
          type="button"
          className="primary"
          onClick={() => void run()}
          disabled={busy || disabled}
        >
          {busy ? "Starting…" : label}
        </button>
      </div>
      {started && (
        <p className="dw-settings-ok">✓ Backfill started — watch the tasks panel.</p>
      )}
    </>
  );
}

function GooglePanel({
  status,
  reload,
  setErr,
}: {
  status: GoogleDataStatus;
  reload: () => Promise<void>;
  setErr: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  const disconnect = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await disconnectGoogleData();
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status.configured) {
    return (
      <Panel title="Google Analytics">
        <p className="dw-muted">
          Google OAuth not configured — set <code>google.clientId</code> and{" "}
          <code>google.clientSecret</code> in Settings.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="Google Analytics">
      {status.connected ? (
        <>
          <p className="dw-settings-ok">
            ✓ Connected as <strong>{status.email ?? "unknown"}</strong>{" "}
            <button type="button" onClick={() => void disconnect()} disabled={busy}>
              Disconnect
            </button>
          </p>
          <BackfillButton target="ga" label="Backfill Analytics" setErr={setErr} />
          <p className="dw-muted">
            Pulls GA properties and daily metrics into{" "}
            <code>@source: dwstats</code>. Google Ads has its own backfill on
            its tile.
          </p>
        </>
      ) : (
        <p className="dw-settings-off">
          Not connected.{" "}
          <a href="/auth/google-data/connect?return=/_apps" className="dw-link">
            Connect Google account
          </a>
        </p>
      )}
    </Panel>
  );
}

function GadsPanel({
  tokenSet,
  gaStatus,
  hasAdsScope,
  reload,
  setErr,
}: {
  tokenSet: boolean;
  gaStatus: GoogleDataStatus;
  hasAdsScope: boolean;
  reload: () => Promise<void>;
  setErr: (e: string | null) => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    if (!draft.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await saveGadsDevToken(draft.trim());
      setDraft("");
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await clearGadsDevToken();
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Google Ads">
      {gaStatus.connected && !hasAdsScope && (
        <p className="dw-settings-off">
          Google account connected but missing the Ads scope.{" "}
          <a href="/auth/google-data/connect?return=/_apps" className="dw-link">
            Reconnect to grant access
          </a>
        </p>
      )}
      {tokenSet ? (
        <>
          <p className="dw-settings-ok">
            ✓ Developer token set{" "}
            <button type="button" onClick={() => void clear()} disabled={busy}>
              Clear
            </button>
          </p>
          <BackfillButton
            target="ads"
            label="Backfill Ads"
            disabled={!gaStatus.connected}
            setErr={setErr}
          />
          <p className="dw-muted">
            Pulls customers, campaigns, and daily stats into{" "}
            <code>@source: dwstats</code> (<code>gads_*</code> tables). A
            failure here is a task error, not a silent skip.
          </p>
        </>
      ) : (
        <>
          <p className="dw-muted" style={{ marginTop: 0 }}>
            Required to use the Google Ads API. Get yours from your Google Ads
            Manager Account → Tools &amp; Settings → Setup → API Center.
          </p>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              type="password"
              className="dw-field-input dw-field-mono"
              placeholder="Paste developer token…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
              }}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="primary"
              onClick={() => void save()}
              disabled={busy || !draft.trim()}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

function AppPanel({
  client,
  onDeleted,
  setErr,
}: {
  client: OAuthClient;
  onDeleted: () => Promise<void>;
  setErr: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  const remove = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await deleteOAuthClient(client.clientId);
      await onDeleted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={client.name}>
      <p className="dw-muted" style={{ marginTop: 0 }}>
        External app — queries datawiki over OAuth (PKCE, <code>read</code>{" "}
        scope: existing queries only, never raw SQL).
      </p>
      <div className="dw-field-mono dw-muted">{client.clientId}</div>
      {client.redirectUris.map((u) => (
        <div key={u} className="dw-muted" style={{ fontSize: "0.85em" }}>
          ↳ {u}
        </div>
      ))}
      <div className="dw-settings-actions">
        <button type="button" onClick={() => void remove()} disabled={busy}>
          Delete app
        </button>
      </div>
    </Panel>
  );
}

function CreateAppPanel({
  onCreated,
  setErr,
}: {
  onCreated: (clientId: string) => Promise<void>;
  setErr: (e: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [redirect, setRedirect] = useState("");
  const [busy, setBusy] = useState(false);

  const register = async (): Promise<void> => {
    if (!name.trim() || !redirect.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await registerOAuthClient(name.trim(), [redirect.trim()]);
      setName("");
      setRedirect("");
      await onCreated(created.clientId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Create App">
      <p className="dw-muted" style={{ marginTop: 0 }}>
        Register a third-party app to query datawiki over OAuth (PKCE, no
        secret). It gets a <code>client_id</code>, runs existing queries as the
        user who authorizes it, and never sends raw SQL. See{" "}
        <code>docs/auth/ExternalApps.md</code>.
      </p>
      <div
        style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}
      >
        <input
          className="dw-field-input"
          placeholder="App name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="dw-field-input dw-field-mono"
          placeholder="https://app.example.com/ (redirect URI)"
          value={redirect}
          onChange={(e) => setRedirect(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void register();
          }}
          style={{ flex: 1, minWidth: "16rem" }}
        />
        <button
          type="button"
          className="primary"
          onClick={() => void register()}
          disabled={busy || !name.trim() || !redirect.trim()}
        >
          {busy ? "…" : "Register"}
        </button>
      </div>
    </Panel>
  );
}
